import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FINISHES, CATEGORIES, CONDITIONS, MATERIALS, SURFACE_FINISHES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Plus, Loader2 } from "lucide-react";

const newKey = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const toEditArticle = (a) => ({
  _key: newKey(),
  article_name: a.article_name || "", category: a.category || "", quantity: String(a.quantity ?? 1),
  base_material: a.base_material || "", weight: a.weight != null ? String(a.weight) : "",
  condition: a.condition || "", surface_finish: a.surface_finish || "",
  finishes: Array.isArray(a.required_finishes) ? a.required_finishes : [], finish_other: a.finish_other || "",
});

const emptyArticle = () => ({ _key: newKey(), article_name: "", category: "", quantity: "1", base_material: "", weight: "", condition: "", surface_finish: "", finishes: [], finish_other: "" });

function validate(articles, form) {
  for (let i = 0; i < articles.length; i++) {
    const a = articles[i];
    if (!a.article_name.trim()) return `Article ${i + 1}: product name is required`;
    if (!a.category) return `Article ${i + 1}: please select a product category`;
    if (!a.quantity || parseInt(a.quantity) < 1) return `Article ${i + 1}: quantity is required (min 1)`;
    if ((a.finishes || []).length === 0) return `Article ${i + 1}: select at least one required finish / service`;
    if ((a.finishes || []).includes("Other") && !a.finish_other.trim()) return `Article ${i + 1}: please describe the custom finish`;
  }
  const est = parseFloat(form.estimated_value || "0");
  const adv = parseFloat(form.advance_payment || "0");
  if (form.advance_payment !== "" && form.estimated_value !== "" && adv > est) return "Advance payment cannot exceed the estimated value";
  return null;
}

export default function EditJobDialog({ job, open, onOpenChange, onSaved }) {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState({ special_instructions: "", estimated_value: "", advance_payment: "", expected_completion_date: "", staff_remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    setArticles((job.articles || []).map(toEditArticle));
    setForm({
      special_instructions: job.special_instructions || "",
      estimated_value: job.estimated_value != null ? String(job.estimated_value) : "",
      advance_payment: job.advance_payment != null ? String(job.advance_payment) : "",
      expected_completion_date: job.expected_completion_date || "",
      staff_remarks: job.staff_remarks || "",
    });
  }, [open, job]);

  const setArticle = (i, k, v) => setArticles((p) => p.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const toggleFinish = (i, f) => setArticles((p) => p.map((a, idx) => idx === i
    ? { ...a, finishes: (a.finishes || []).includes(f) ? a.finishes.filter((x) => x !== f) : [...(a.finishes || []), f] } : a));
  const addArticle = () => setArticles((p) => [...p, emptyArticle()]);
  const removeArticle = (i) => setArticles((p) => p.filter((_, idx) => idx !== i));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const err = validate(articles, form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        articles: articles.map((a) => ({
          article_name: a.article_name.trim(), category: a.category, quantity: parseInt(a.quantity),
          base_material: a.base_material, weight: (a.weight || "").trim(), condition: a.condition, surface_finish: a.surface_finish,
          required_finishes: a.finishes || [], finish_other: a.finish_other.trim(),
        })),
        special_instructions: form.special_instructions.trim(),
        estimated_value: form.estimated_value !== "" ? parseFloat(form.estimated_value) : null,
        advance_payment: form.advance_payment !== "" ? parseFloat(form.advance_payment) : null,
        expected_completion_date: form.expected_completion_date || "",
        staff_remarks: form.staff_remarks.trim(),
      };
      await api.put(`/jobs/${job.id}`, payload);
      toast.success("Job details updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update job");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-job-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Edit Job — {job?.job_number}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold mb-2">Articles</h3>
            <div className="space-y-3">
              {articles.map((art, i) => (
                <div key={art._key} className="rounded-xl border p-3.5 space-y-3" data-testid={`edit-article-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Article {i + 1}</p>
                    {articles.length > 1 && (
                      <button type="button" onClick={() => removeArticle(i)} data-testid={`edit-article-remove-${i}`} className="text-red-500 p-1" aria-label="Remove article">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div>
                    <Label>Article / Product Name *</Label>
                    <Input data-testid={`edit-article-name-input-${i}`} value={art.article_name} onChange={(e) => setArticle(i, "article_name", e.target.value)} className="h-11 mt-1" placeholder="e.g. Brass Tiffin Box" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Product Category *</Label>
                      <Select value={art.category} onValueChange={(v) => setArticle(i, "category", v)}>
                        <SelectTrigger data-testid={`edit-article-category-select-${i}`} className="h-11 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Quantity *</Label>
                      <Input data-testid={`edit-article-quantity-input-${i}`} type="number" min="1" inputMode="numeric" value={art.quantity} onChange={(e) => setArticle(i, "quantity", e.target.value)} className="h-11 mt-1" />
                    </div>
                    <div>
                      <Label>Base Material</Label>
                      <Select value={art.base_material} onValueChange={(v) => setArticle(i, "base_material", v)}>
                        <SelectTrigger data-testid={`edit-article-material-select-${i}`} className="h-11 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Weight (Kg)</Label>
                      <Input data-testid={`edit-article-weight-input-${i}`} inputMode="decimal" value={art.weight} onChange={(e) => setArticle(i, "weight", e.target.value.replace(/[^\d.]/g, ""))} className="h-11 mt-1" placeholder="e.g. 1.5" />
                    </div>
                    <div>
                      <Label>Current Condition</Label>
                      <Select value={art.condition} onValueChange={(v) => setArticle(i, "condition", v)}>
                        <SelectTrigger data-testid={`edit-article-condition-select-${i}`} className="h-11 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Buffing Surface Finish</Label>
                      <Select value={art.surface_finish} onValueChange={(v) => setArticle(i, "surface_finish", v)}>
                        <SelectTrigger data-testid={`edit-article-surface-finish-select-${i}`} className="h-11 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{SURFACE_FINISHES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Required Service / Finish *</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid={`edit-finish-options-${i}`}>
                      {FINISHES.map((f) => (
                        <button key={f} type="button" onClick={() => toggleFinish(i, f)}
                          data-testid={`edit-finish-chip-${i}-${f.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            (art.finishes || []).includes(f) ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent"
                          }`}>
                          {f}
                        </button>
                      ))}
                    </div>
                    {(art.finishes || []).includes("Other") && (
                      <Input data-testid={`edit-finish-other-input-${i}`} value={art.finish_other} onChange={(e) => setArticle(i, "finish_other", e.target.value)} className="h-10 mt-2" placeholder="Describe the custom requirement" />
                    )}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addArticle} data-testid="edit-add-article-button" className="w-full h-11 border-dashed font-semibold">
                <Plus className="h-4 w-4 mr-1.5" /> Add Another Article
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estimated Value (₹)</Label>
              <Input data-testid="edit-estimated-value-input" inputMode="decimal" value={form.estimated_value} onChange={(e) => setField("estimated_value", e.target.value.replace(/[^\d.]/g, ""))} className="h-11 mt-1" placeholder="Optional" />
            </div>
            <div>
              <Label>Advance Payment (₹)</Label>
              <Input data-testid="edit-advance-payment-input" inputMode="decimal" value={form.advance_payment} onChange={(e) => setField("advance_payment", e.target.value.replace(/[^\d.]/g, ""))} className="h-11 mt-1" placeholder="Optional" />
            </div>
            <div className="col-span-2">
              <Label>Expected Completion Date</Label>
              <Input data-testid="edit-expected-date-input" type="date" value={form.expected_completion_date} onChange={(e) => setField("expected_completion_date", e.target.value)} className="h-11 mt-1" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">All amounts are exclusive of tax. Applicable tax will be charged additionally.</p>

          <div>
            <Label>Special Instructions (customer)</Label>
            <Textarea data-testid="edit-special-instructions-input" value={form.special_instructions} onChange={(e) => setField("special_instructions", e.target.value)} rows={2} className="mt-1" />
          </div>
          <div>
            <Label>Staff Remarks (internal)</Label>
            <Textarea data-testid="edit-staff-remarks-input" value={form.staff_remarks} onChange={(e) => setField("staff_remarks", e.target.value)} rows={2} className="mt-1" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-job-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="font-semibold" data-testid="edit-job-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
